import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Language = "ru" | "kz";

// Only the static UI chrome (nav, buttons, footer) is translated — product
// names/descriptions come from Russian source documents (price lists) and
// stay in Russian regardless of the selected language, since translating
// hundreds of product entries is a separate, much larger effort.
export const TRANSLATIONS = {
  ru: {
    portfolio: "Портфолио",
    equipment: "Оборудование",
    services: "Услуги",
    about: "О компании",
    contacts: "Контакты",
    catalog: "Каталог",
    searchPlaceholder: "Поиск оборудования",
    searchToggle: "Переключить поиск",
    searchClear: "Очистить поиск",
    inProject: "В проекте",
    login: "Вход / регистрация",
    adminPanel: "Панель управления",
    logout: "Выйти",
    menuToggle: "Переключить меню",
    footerCompany: "ТОО «БАРКРАФТ (BARCRAFT)»",
    footerRights: "Все права защищены",
    cookieBannerText:
      "Мы используем файлы cookie, чтобы каталог показывал товары, которые могут быть вам интересны. Необходимые cookie для входа в личный кабинет используются всегда.",
    cookieAccept: "Принять",
    cookieDecline: "Отклонить",
    cookieLearnMore: "Подробнее",
    footerCookieSettings: "Cookie-настройки",

    footerBinLabel: "БИН:",
    footerDocShipping: "Доставка и оплата",
    footerLinkRefund: "Гарантия и возврат",
    footerLinkTerms: "Оферта",
    footerLinkPrivacy: "Конфиденциальность",
    footerQuoteHeading: "Собрать коммерческое предложение",
    footerQuoteDesc: "Добавьте нужное оборудование в проект — подготовим предложение под ваш запрос.",
    footerQuoteButton: "Смотреть список проекта",

    // Hero
    heroStat1Value: "200+",
    heroStat1Label: "Оснащённых кухонь",
    heroStat2Value: "160+",
    heroStat2Label: "Мировых брендов",
    heroStat3Value: "15+ лет",
    heroStat3Label: "Опыт на рынке",
    heroTitleLine1: "Проектируем",
    heroTitleLine2: "успех вашего бизнеса",
    heroSubtitle:
      "Комплексное оснащение ресторанов, баров и профессиональных кухонь оборудованием ведущих мировых брендов.",
    heroCtaPrimary: "Изучить оборудование",
    heroCtaSecondary: "Запросить консультацию",

    // Services
    servicesHeading: "Полный цикл — от консультации до гарантии",
    serviceStepLabel: "Шаг",
    service1Title: "Консультация инженера",
    service1Desc:
      "Свяжитесь с нами для обсуждения вашего проекта. Мы проконсультируем вас по техническим вопросам и поможем подобрать идеальное решение под ваш бюджет и концепцию.",
    service2Title: "Проект и договор",
    service2Desc:
      "Технологическое проектирование, зонирование и расстановка оборудования закрепляются договором с точными сроками и условиями поставки.",
    service3Title: "Производство",
    service3Desc:
      "Изготовление и поставка оборудования от официальных партнёров — мировых лидеров пищевого машиностроения.",
    service4Title: "Монтаж и гарантия",
    service4Desc:
      "Монтаж, пусконаладка и сервисное обслуживание с гарантией на всё поставленное оборудование.",
    service5Title: "Обучение персонала",
    service5Desc:
      "Проводим инструктаж и обучение вашего персонала работе с оборудованием для безопасной и эффективной эксплуатации.",

    // About
    aboutTitle: "Комплексное оснащение",
    aboutDescription:
      "BARCRAFT (ТОО «БАРКРАФТ») специализируется на проектировании и комплексном оснащении заготовочных производств, фабрик-кухонь, пищевых комбинатов, индустриальных столовых, медицинских учреждений и пекарен под ключ — от технологического проектирования и зонирования до поставки оборудования, монтажа и сервисного обслуживания. Официальный дилер 160 мировых брендов пищевого машиностроения.",

    // CtaBanner
    ctaHeading: "От идеи до полного запуска",
    ctaDescription:
      "Сопровождаем на каждом этапе — от технологического проектирования и подбора оборудования до монтажа, пусконаладки и обучения персонала.",
    ctaButton: "Оставить заявку",

    // Portfolio
    portfolioHeading: "Проекты, которыми мы гордимся",
    portfolioDescription:
      "Реализованные объекты HoReCa — от технологического проектирования до полного оснащения и запуска.",
    portfolioPrevAria: "Предыдущий проект",
    portfolioNextAria: "Следующий проект",

    // TopBar
    topBarCta: "Проконсультируйтесь с нами",

    // Blog
    blogSectionHeading: "Наши последние новости",
    blogViewAll: "Смотреть все",
    blogPageTitle: "Блог",
    blogPageDescription:
      "Разборы оборудования и советы по оснащению профессиональных кухонь от инженеров BARCRAFT.",
    blogReadMore: "Читать далее",

    // Brands
    brandsTitle: "Официальный дилер мировых брендов",
    brandsDescription:
      "BARCRAFT сотрудничает напрямую с ведущими производителями пищевого машиностроения, обеспечивая оригинальное оборудование, заводскую гарантию и доступ к сервисной поддержке.",

    // Themes
    themesPageTitle: "Оборудование под ваш сценарий",
    themesPageDescription:
      "Готовые направления комплектации в зависимости от типа вашего проекта. Выберите тему, чтобы перейти к подходящим категориям оборудования.",
    theme1Title: "Открытие ресторана",
    theme1Desc:
      "Полное оснащение кухни с нуля — от теплового и холодильного оборудования до сервировки и мебели из нержавеющей стали.",
    theme2Title: "Модернизация фабрики-кухни",
    theme2Desc:
      "Замена и расширение производственных линий для индустриальных столовых и комбинатов питания.",
    theme3Title: "Кейтеринг и мероприятия",
    theme3Desc:
      "Мобильные тележки, термоконтейнеры и сервировка для выездного обслуживания и банкетов.",
    theme4Title: "Пекарня и кондитерская",
    theme4Desc:
      "Специализированное тепловое и холодильное оборудование для выпечки, расстойки и хранения.",

    // Sector
    sectorEyebrow: "Отраслевое решение",
    sectorEquipmentHeading: "Комплектация для этого сектора",

    // Contact
    contactDetailAddress: "Адрес",
    contactDetailAddressValue:
      "Республика Казахстан, г. Астана, район Алматы, ул. 101, здание 49Б, 010011",
    contactDetailPhone: "Телефон",
    contactDetailEmail: "Email",
    contactDetailSocial: "Соцсети",
    contactDetailHours: "Часы работы",
    contactDetailHoursValue: "Пн – Пт: 09:00 – 18:00 (время Астаны / GMT+5)",
    contactPageTitle: "Обсудим ваш проект",
    contactPageDescription:
      "Наши инженеры готовы подобрать оборудование под ваши задачи и подготовить коммерческое предложение в течение одного рабочего дня.",
    contactSuccessTitle: "Сообщение отправлено",
    contactSuccessDesc: "Мы свяжемся с вами в ближайшее время.",
    contactSuccessButton: "Отправить ещё одно сообщение",
    contactFormName: "Имя",
    contactFormEmail: "Email",
    contactFormCompany: "Компания",
    contactFormMessage: "Сообщение",
    contactFormSubmit: "Отправить сообщение",
    contactFormError: "Не удалось отправить сообщение. Попробуйте ещё раз.",

    // Catalog / ProductMatrix chrome
    catalogBannerHeading: "Каталог оборудования",
    catalogSearchPlaceholder: "Поиск по ключевым словам",
    filtersButton: "Фильтры",
    catalogCloseFiltersAria: "Закрыть фильтры",
    catalogPrevPageAria: "Предыдущая страница",
    catalogPageAriaPrefix: "Страница",
    catalogNextPageAria: "Следующая страница",
    catalogFirstPageAria: "Первая страница",
    catalogLastPageAria: "Последняя страница",
    catalogSearchResultsPrefix: "Результаты поиска по запросу",
    catalogEmptySearch: "Ничего не найдено по вашему запросу.",
    catalogEmptyFilters: "Ничего не найдено по выбранным фильтрам.",
    productMatrixHeading: "Оборудование по категориям",
    productMatrixDescription:
      "Просмотрите каталог BARCRAFT и добавьте устройства в список проекта для индивидуального коммерческого предложения.",
    productMatrixFullCatalog: "Полный каталог",

    // Product card / detail
    inStockBadge: "В наличии",
    pricePrefixFrom: "от ",
    priceOnRequest: "Цена по запросу",
    productDetailPriceLabel: "Цена",
    productDetailPriceVatLabel: "с НДС",
    productAdded: "Добавлено",
    productChooseConfig: "Выберите параметры",
    productAddToProject: "+ в проект",
    productAddToProjectFull: "В проект",
    productImageLabel: "Фото",
    quantityDecrease: "Уменьшить количество",
    quantityIncrease: "Увеличить количество",
    quantityLabel: "Количество",
    productCompatibleWithLabel: "Совместимо с",
    productLoading: "Загрузка…",
    productDetailTabDescription: "Описание",
    productDetailTabSpecs: "Характеристики",
    productDetailAdditionalInfo: "Дополнительная информация",
    productDetailApplications: "Применение",
    productDetailContainerTypes: "Типы тары",
    productDetailTabVideo: "Видео",
    productDetailTabBrochure: "Брошюра",
    productDetailTabAccessories: "Аксессуары",
    productDetailSimilarItems: "Похожие товары",
    productDetailPrevItems: "Предыдущие товары",
    productDetailNextItems: "Следующие товары",
    productDetailExpandImage: "Открыть изображение на весь экран",
    productDetailCloseImage: "Закрыть",
    catalogLabel: "Каталог",
    breadcrumbHome: "Главная",
    breadcrumbSectors: "Секторы",
    productDetailCapacityGn: "Вместимость (GN)",
    productDetailClarifyStock: "Уточнить наличие",
    productDetailStockPrefix: "В наличии:",
    productDetailStockUnit: "шт.",
    productDetailBackorder: "Под заказ",
    productDetailLeadTimePrefix: "Срок доставки:",
    productDetailLeadTimeUnit: "дней",
    productDetailReassurance:
      "Наши инженеры подтвердят наличие и подготовят коммерческое предложение в течение одного рабочего дня.",
    productDetailAllBrandProducts: "Все товары",
    productDetailAllSubcategoryPrefix: "Все",
    productDetailInCategorySuffix: "в категории",
    productDetailDownloadBrochure: "Скачать брошюру",
    productDetailBrochurePdf: "PDF с полным каталогом линии",
    productDetailBrochureNoPreview: "Браузер не может показать PDF прямо на странице.",
    productDetailOpenBrochureNewTab: "Открыть брошюру в новой вкладке",
    productDetailDeliveryKz: "Доставка по Казахстану",
    productDetailDeliveryTerms: "Срок и стоимость — в коммерческом предложении",
    productDetailCareTitle: "С заботой на каждом этапе",
    productDetailCareDesc: "Сопровождение от подбора оборудования до запуска",
    productDetailWarrantyTitle: "Гарантия производителя",
    productDetailWarrantyDesc: "Официальная гарантия и сервисная поддержка",
    productDetailEngineerConsultDesc: "Поможем подобрать и настроить оборудование под задачу",

    // RFQ drawer
    rfqFieldRequired: "Заполните это поле",
    rfqEmailInvalid: "Введите корректный email",
    rfqInProjectPrefix: "В проекте",
    rfqYourDetails: "Ваши данные",
    rfqCloseAria: "Закрыть список проектов",
    rfqSuccessTitle: "Запрос на коммерческое предложение отправлен",
    rfqSuccessDesc:
      "Инженер-консультант BARCRAFT свяжется с вами с коммерческим предложением в течение одного рабочего дня.",
    rfqClose: "Закрыть",
    rfqEmptyCart:
      "Оборудование пока не добавлено. Просмотрите каталог и добавьте позиции в список проекта.",
    rfqDecreaseQtyAria: "Уменьшить количество",
    rfqIncreaseQtyAria: "Увеличить количество",
    rfqRemoveAria: "Удалить",
    rfqPriceOnRequestShort: "По запросу",
    rfqTotal: "Итого",
    rfqContinue: "Продолжить",
    rfqBackToListText: "Назад к списку",
    rfqItemSingular: "позиция",
    rfqItemPlural: "позиций",
    rfqFormName: "Имя",
    rfqFormCompany: "Компания",
    rfqFormPhone: "Телефон",
    rfqFormEmail: "Контактный email",
    rfqFormNotes: "Заметки по проекту",
    rfqSubmit: "Запросить счёт",
    rfqFormError: "Не удалось отправить запрос. Попробуйте ещё раз.",
    rfqAccountMissingInfoError:
      "В вашем аккаунте не заполнены контактные данные. Свяжитесь с нами по телефону +7 771 182 93 08, чтобы отправить запрос.",
    rfqSessionExpiredError: "Сессия истекла. Заполните контактные данные ниже, чтобы отправить запрос.",

    // Auth (Login / Register / Reset)
    authPasswordMismatch: "Пароли не совпадают",
    authCheckEmailTitle: "Проверьте почту",
    authCheckEmailMessage:
      "Мы отправили письмо с подтверждением на {email}. Перейдите по ссылке в письме, чтобы завершить регистрацию.",
    authResendVerification: "Отправить письмо ещё раз",
    authResendSentMessage: "Письмо отправлено повторно.",
    authEmailNotVerifiedError: "Подтвердите email, чтобы войти.",
    authRegisterEmailSendError:
      "Аккаунт создан, но письмо с подтверждением не отправлено. Пожалуйста, свяжитесь с нами, чтобы завершить регистрацию.",
    verifyEmailLoading: "Подтверждаем ваш email…",
    verifyEmailInvalidTitle: "Ссылка недействительна",
    verifyEmailInvalidDescription:
      "Эта ссылка истекла или уже была использована. Запросите новую ссылку ниже.",
    verifyEmailResendIntro: "Введите email, чтобы получить новую ссылку для подтверждения.",
    authEmailFormatHint: "Введите email в формате name@company.com",
    rfqLoginRequiredMessage: "Войдите в личный кабинет, чтобы отправить запрос коммерческого предложения.",
    rfqLoginRequiredCta: "Войти",
    rfqGuestLoginHint: "Уже есть аккаунт?",
    rfqGuestLoginCta: "Войдите",
    loginHeroTitle1: "Присоединяйтесь к нам",
    loginHeroTitle2: "за премиальными кухонными решениями",
    loginHeroDesc:
      "Сохраняйте список оборудования, возвращайтесь к нему с любого устройства и получайте персональные рекомендации под ваш проект.",
    authTabLogin: "Вход",
    authTabRegister: "Регистрация",
    authEmailLabel: "Email",
    authPasswordLabel: "Пароль",
    authForgotPassword: "Забыли пароль?",
    authSubmitLogin: "Войти",
    authNameLabel: "Имя / Фамилия",
    authPhoneLabel: "Номер телефона",
    authCompanyLabel: "Компания / Ресторан",
    authConfirmPasswordLabel: "Подтвердите пароль",
    authSubmitRegister: "Зарегистрироваться",
    authBackToLogin: "Назад ко входу",
    authResetSentMessage:
      "Если аккаунт с email «{email}» существует, мы отправили на него ссылку для восстановления пароля. Проверьте почту.",
    authForgotIntro:
      "Укажите email, указанный при регистрации — мы отправим ссылку для восстановления пароля.",
    authSubmitSendLink: "Отправить ссылку",
    authAdminLoginPrefix: "Вход для администратора —",
    authAdminLoginLinkText: "здесь",
    resetPageTitle: "Новый пароль",
    resetHeroTitle1: "Верните доступ",
    resetHeroTitle2: "к своему кабинету",
    resetHeroDesc:
      "Задайте новый пароль — после сохранения вы сможете войти с любого устройства.",
    resetInvalidTitle: "Ссылка недействительна",
    resetInvalidDescription:
      "Эта ссылка истекла или уже была использована. Запросите восстановление пароля ещё раз на странице входа.",
    resetInvalidLinkPrefix: "Ссылка недействительна. Запросите восстановление пароля ещё раз на",
    resetLoginPageLinkText: "странице входа",
    resetSuccessTitle: "Пароль изменён",
    resetSuccessMessage: "Пароль успешно изменён. Теперь вы можете войти с новым паролем.",
    resetToLoginLink: "Ко входу",
    resetNewPasswordLabel: "Новый пароль",
    resetPasswordHint: "Не менее 6 символов",
    resetShowPassword: "Показать пароль",
    resetHidePassword: "Скрыть пароль",
    resetPasswordsMatch: "Пароли совпадают",
    resetSubmitting: "Сохранение…",
    resetSubmit: "Сохранить новый пароль",

    // Account
    accountPageTitle: "Личный кабинет",
    accountLoggedInAs: "Вы вошли как",
    accountRecommendationsTitle: "Рекомендуем вам",
    accountRecommendationsEmpty:
      "Пока нет персональных рекомендаций. Посмотрите каталог, и мы подберём подходящее оборудование.",
    accountRequestsTitle: "Мои запросы",
    accountRequestsEmpty: "У вас пока нет запросов. Добавьте оборудование в проект и отправьте запрос коммерческого предложения.",
    accountRequestItemsCount: "Позиций:",
    accountRequestDownload: "Скачать предложение",
    accountRequestsColumnRequest: "Запрос",
    accountRequestsColumnDate: "Дата",
    accountRequestsColumnAmount: "Сумма",
    accountRequestsColumnStatus: "Статус",
    accountRequestsColumnDocuments: "Счета / Документы",
    accountRequestsColumnDetails: "Детали",
    accountRequestDocumentsPending: "В подготовке",
    requestStatusNew: "В обработке",
    requestStatusInReview: "На рассмотрении",
    requestStatusOfferSent: "Предложение готово",
    requestStatusCancelled: "Отменён",
    accountRequestCancel: "Отменить запрос",
    accountRequestCancelConfirm: "Отменить этот запрос? Это действие нельзя отменить.",
    accountRequestCancelError: "Не удалось отменить запрос. Попробуйте ещё раз.",

    // Catalog toolbar / sidebar
    rangeFrom: "от",
    rangeTo: "до",
    resultsShownPrefix: "Показано",
    resultsShownOf: "из",
    productCountSuffix: "товаров",
    sortLabel: "Сортировка",
    sortRelevance: "По релевантности",
    sortPriceAsc: "Сначала дешевле",
    sortPriceDesc: "Сначала дороже",
    sortNameAsc: "По названию (А-Я)",
    sortNameDesc: "По названию (Я-А)",
    sortNewest: "Новинки",
    clearAllFilters: "Очистить всё",
    categoryFacetTitle: "Категория",
    brandFacetTitle: "Бренд",
    characteristicsFacetTitle: "Характеристики",
    collapseFacet: "Свернуть",
    expandFacet: "Показать ещё",
    brandSearchPlaceholder: "Поиск бренда",
    brandSearchNoResults: "Ничего не найдено",
    brandPickerTrigger: "Выбрать бренд",
    recommendedLabel: "Все товары",

    // Misc toasts / errors
    rfqToastAdded: "Добавлено в проект",
    authGenericError: "Не удалось выполнить запрос",

    // 404
    notFoundEyebrow: "404",
    notFoundTitle: "Страница не найдена",
    notFoundDescription: "Возможно, страница была перемещена или больше не существует. Проверьте адрес или вернитесь на главную.",
    notFoundCtaHome: "На главную",
    notFoundCtaCatalog: "Смотреть каталог",
  },
  kz: {
    portfolio: "Портфолио",
    equipment: "Жабдықтар",
    services: "Қызметтер",
    about: "Компания туралы",
    contacts: "Байланыстар",
    catalog: "Каталог",
    searchPlaceholder: "Жабдықтарды іздеу",
    searchToggle: "Іздеуді ауыстыру",
    searchClear: "Іздеуді тазалау",
    inProject: "Жобада",
    login: "Кіру / тіркелу",
    adminPanel: "Басқару тақтасы",
    logout: "Шығу",
    menuToggle: "Мәзірді ауыстыру",
    footerCompany: "«БАРКРАФТ (BARCRAFT)» ЖШС",
    footerRights: "Барлық құқықтар қорғалған",
    cookieBannerText:
      "Каталог сізге қызықты болуы мүмкін тауарларды көрсетуі үшін cookie файлдарын қолданамыз. Жеке кабинетке кіру үшін қажетті cookie файлдары әрқашан қолданылады.",
    cookieAccept: "Қабылдау",
    cookieDecline: "Бас тарту",
    cookieLearnMore: "Толығырақ",
    footerCookieSettings: "Cookie параметрлері",

    footerBinLabel: "БСН:",
    footerDocShipping: "Жеткізу және төлем",
    footerLinkRefund: "Кепілдік және қайтару",
    footerLinkTerms: "Оферта",
    footerLinkPrivacy: "Құпиялылық",
    footerQuoteHeading: "Коммерциялық ұсыныс жинау",
    footerQuoteDesc: "Қажетті жабдықты жобаға қосыңыз — сұранысыңызға сай ұсыныс дайындаймыз.",
    footerQuoteButton: "Жоба тізімін қарау",

    // Hero
    heroStat1Value: "200+",
    heroStat1Label: "Жабдықталған асханалар",
    heroStat2Value: "160+",
    heroStat2Label: "Әлемдік бренд-серіктестер",
    heroStat3Value: "15+ жыл",
    heroStat3Label: "Нарықтағы тәжірибе",
    heroTitleLine1: "Жобалаймыз",
    heroTitleLine2: "бизнесіңіздің табысын",
    heroSubtitle:
      "Мейрамханалар, барлар және кәсіби асханаларды әлемнің жетекші брендтерінің жабдықтарымен кешенді жабдықтау.",
    heroCtaPrimary: "Жабдықтарды қарау",
    heroCtaSecondary: "Кеңес сұрау",

    // Services
    servicesHeading: "Толық цикл — кеңесуден кепілдікке дейін",
    serviceStepLabel: "Қадам",
    service1Title: "Инженер кеңесі",
    service1Desc:
      "Жобаңызды талқылау үшін бізбен байланысыңыз. Біз сізге техникалық сұрақтар бойынша кеңес береміз және бюджетіңіз бен тұжырымдамаңызға сай ең тиімді шешімді таңдауға көмектесеміз.",
    service2Title: "Жоба және шарт",
    service2Desc:
      "Технологиялық жобалау, аймақтарға бөлу және жабдықты орналастыру нақты мерзімдер мен жеткізу шарттары көрсетілген шартпен бекітіледі.",
    service3Title: "Өндіру",
    service3Desc:
      "Жабдықты ресми серіктестерден — тамақ өнеркәсібі машина жасаудың әлемдік көшбасшыларынан — дайындау және жеткізу.",
    service4Title: "Монтаж және кепілдік",
    service4Desc:
      "Жеткізілген барлық жабдыққа кепілдікпен монтаж, іске қосу-баптау жұмыстары және сервистік қызмет көрсету.",
    service5Title: "Персоналды оқыту",
    service5Desc:
      "Жабдықты қауіпсіз және тиімді пайдалану үшін персоналыңызға нұсқаулық беру және оқыту жүргіземіз.",

    // About
    aboutTitle: "Кешенді жабдықтау",
    aboutDescription:
      "BARCRAFT («БАРКРАФТ» ЖШС) дайындау өндірістерін, фабрика-асханаларды, тамақ комбинаттарын, индустриялық асханаларды, медициналық мекемелер мен нан пісіру орындарын кілт тапсыру негізінде жобалау және кешенді жабдықтауға маманданған — технологиялық жобалау мен аймақтарға бөлуден бастап жабдық жеткізу, монтаждау және сервистік қызмет көрсетуге дейін. Тамақ өнеркәсібі машина жасаудың 160 әлемдік брендінің ресми дилері.",

    // CtaBanner
    ctaHeading: "Асхананы жобаңызға сай жабдықтаймыз",
    ctaDescription:
      "Технологиялық жобалау мен жабдықты таңдаудан бастап монтаж, іске қосу-баптау және персоналды оқытуға дейін әр кезеңде қолдау көрсетеміз.",
    ctaButton: "Өтінім қалдыру",

    // Portfolio
    portfolioHeading: "Біз мақтан тұтатын жобалар",
    portfolioDescription:
      "Іске асырылған HoReCa нысандары — технологиялық жобалаудан толық жабдықтау мен іске қосуға дейін.",
    portfolioPrevAria: "Алдыңғы жоба",
    portfolioNextAria: "Келесі жоба",

    // TopBar
    topBarCta: "Бізден кеңес алыңыз",

    // Blog
    blogSectionHeading: "Соңғы жаңалықтарымыз",
    blogViewAll: "Барлығын көру",
    blogPageTitle: "Блог",
    blogPageDescription:
      "BARCRAFT инженерлерінен жабдық шолулары және кәсіби асханаларды жабдықтау бойынша кеңестер.",
    blogReadMore: "Толығырақ оқу",

    // Brands
    brandsTitle: "Әлемдік брендтердің ресми дилері",
    brandsDescription:
      "BARCRAFT тамақ өнеркәсібі машина жасаудың жетекші өндірушілерімен тікелей жұмыс істейді, түпнұсқа жабдықты, зауыттық кепілдікті және сервистік қолдауға қолжетімділікті қамтамасыз етеді.",

    // Themes
    themesPageTitle: "Сіздің сценарийіңізге арналған жабдық",
    themesPageDescription:
      "Жобаңыздың түріне байланысты дайын жабдықтау бағыттары. Сәйкес жабдық санаттарына өту үшін тақырыпты таңдаңыз.",
    theme1Title: "Мейрамхана ашу",
    theme1Desc:
      "Асхананы нөлден толық жабдықтау — жылу және суық жабдықтан бастап тот баспайтын болаттан жасалған сервировка мен жиһазға дейін.",
    theme2Title: "Фабрика-асхананы жаңғырту",
    theme2Desc:
      "Индустриялық асханалар мен тамақ комбинаттары үшін өндірістік желілерді ауыстыру және кеңейту.",
    theme3Title: "Кейтеринг және іс-шаралар",
    theme3Desc:
      "Көшпелі қызмет көрсету мен банкеттерге арналған жылжымалы арбашалар, термоконтейнерлер және сервировка.",
    theme4Title: "Наубайхана және кондитер цехы",
    theme4Desc:
      "Пісіру, ашыту және сақтау үшін арнайы жылу және суық жабдық.",

    // Sector
    sectorEyebrow: "Салалық шешім",
    sectorEquipmentHeading: "Осы сектор үшін жабдықтау",

    // Contact
    contactDetailAddress: "Мекенжай",
    contactDetailAddressValue:
      "Қазақстан Республикасы, Астана қаласы, Алматы ауданы, 101-көше, 49Б ғимарат, 010011",
    contactDetailPhone: "Телефон",
    contactDetailEmail: "Email",
    contactDetailSocial: "Әлеуметтік желілер",
    contactDetailHours: "Жұмыс уақыты",
    contactDetailHoursValue: "Дс – Жм: 09:00 – 18:00 (Астана уақыты / GMT+5)",
    contactPageTitle: "Жобаңызды талқылайық",
    contactPageDescription:
      "Біздің инженерлер міндеттеріңізге сай жабдықты таңдап, бір жұмыс күні ішінде коммерциялық ұсыныс дайындауға дайын.",
    contactSuccessTitle: "Хабарлама жіберілді",
    contactSuccessDesc: "Жақын арада сізбен байланысамыз.",
    contactSuccessButton: "Тағы бір хабарлама жіберу",
    contactFormName: "Аты-жөні",
    contactFormEmail: "Email",
    contactFormCompany: "Компания",
    contactFormMessage: "Хабарлама",
    contactFormSubmit: "Хабарламаны жіберу",
    contactFormError: "Хабарламаны жіберу мүмкін болмады. Қайталап көріңіз.",

    // Catalog / ProductMatrix chrome
    catalogBannerHeading: "Жабдықтар каталогы",
    catalogSearchPlaceholder: "Кілт сөздер бойынша іздеу",
    filtersButton: "Сүзгілер",
    catalogCloseFiltersAria: "Сүзгілерді жабу",
    catalogPrevPageAria: "Алдыңғы бет",
    catalogPageAriaPrefix: "Бет",
    catalogNextPageAria: "Келесі бет",
    catalogFirstPageAria: "Бірінші бет",
    catalogLastPageAria: "Соңғы бет",
    catalogSearchResultsPrefix: "Сұрау бойынша іздеу нәтижелері",
    catalogEmptySearch: "Сұранысыңыз бойынша ешнәрсе табылмады.",
    catalogEmptyFilters: "Таңдалған сүзгілер бойынша ешнәрсе табылмады.",
    productMatrixHeading: "Санаттар бойынша жабдық",
    productMatrixDescription:
      "BARCRAFT каталогын қараңыз және жеке коммерциялық ұсыныс алу үшін құрылғыларды жоба тізіміне қосыңыз.",
    productMatrixFullCatalog: "Толық каталог",

    // Product card / detail
    inStockBadge: "Бар",
    pricePrefixFrom: "бастап ",
    priceOnRequest: "Сұраныс бойынша баға",
    productDetailPriceLabel: "Бағасы",
    productDetailPriceVatLabel: "ҚҚС-пен",
    productAdded: "Қосылды",
    productChooseConfig: "Параметрлерді таңдаңыз",
    productAddToProject: "+ жобаға",
    productAddToProjectFull: "Жобаға қосу",
    productImageLabel: "Фото",
    quantityDecrease: "Мөлшерін азайту",
    quantityIncrease: "Мөлшерін көбейту",
    quantityLabel: "Саны",
    productCompatibleWithLabel: "Үйлесімді",
    productLoading: "Жүктелуде…",
    productDetailTabDescription: "Сипаттама",
    productDetailTabSpecs: "Сипаттамалар",
    productDetailAdditionalInfo: "Қосымша ақпарат",
    productDetailApplications: "Қолданылуы",
    productDetailContainerTypes: "Ыдыс түрлері",
    productDetailTabVideo: "Видео",
    productDetailTabBrochure: "Брошюра",
    productDetailTabAccessories: "Аксессуарлар",
    productDetailSimilarItems: "Ұқсас тауарлар",
    productDetailPrevItems: "Алдыңғы тауарлар",
    productDetailNextItems: "Келесі тауарлар",
    productDetailExpandImage: "Суретті толық экранда ашу",
    productDetailCloseImage: "Жабу",
    catalogLabel: "Каталог",
    breadcrumbHome: "Басты бет",
    breadcrumbSectors: "Секторлар",
    productDetailCapacityGn: "Сыйымдылық (GN)",
    productDetailClarifyStock: "Барын нақтылау",
    productDetailStockPrefix: "Қоймада бар:",
    productDetailStockUnit: "дана",
    productDetailBackorder: "Тапсырыс бойынша",
    productDetailLeadTimePrefix: "Жеткізу мерзімі:",
    productDetailLeadTimeUnit: "күн",
    productDetailReassurance:
      "Біздің инженерлер барын растап, бір жұмыс күні ішінде коммерциялық ұсыныс дайындайды.",
    productDetailAllBrandProducts: "Барлық тауарлар",
    productDetailAllSubcategoryPrefix: "Барлық",
    productDetailInCategorySuffix: "санатында",
    productDetailDownloadBrochure: "Брошюраны жүктеу",
    productDetailBrochurePdf: "Желінің толық каталогы бар PDF",
    productDetailBrochureNoPreview: "Браузер PDF файлын бетте тікелей көрсете алмайды.",
    productDetailOpenBrochureNewTab: "Брошюраны жаңа бетте ашу",
    productDetailDeliveryKz: "Қазақстан бойынша жеткізу",
    productDetailDeliveryTerms: "Мерзімі мен құны — коммерциялық ұсыныста",
    productDetailCareTitle: "Әр кезеңде қамқорлықпен",
    productDetailCareDesc: "Жабдықты таңдаудан іске қосуға дейін сүйемелдеу",
    productDetailWarrantyTitle: "Өндіруші кепілдігі",
    productDetailWarrantyDesc: "Ресми кепілдік және сервистік қолдау",
    productDetailEngineerConsultDesc: "Тапсырмаға сай жабдықты таңдап, баптауға көмектесеміз",

    // RFQ drawer
    rfqFieldRequired: "Бұл жолды толтырыңыз",
    rfqEmailInvalid: "Дұрыс email енгізіңіз",
    rfqInProjectPrefix: "Жобада",
    rfqYourDetails: "Сіздің деректеріңіз",
    rfqCloseAria: "Жоба тізімін жабу",
    rfqSuccessTitle: "Коммерциялық ұсыныс сұрауы жіберілді",
    rfqSuccessDesc:
      "BARCRAFT инженер-кеңесшісі сізбен бір жұмыс күні ішінде коммерциялық ұсыныспен байланысады.",
    rfqClose: "Жабу",
    rfqEmptyCart:
      "Әзірге жабдық қосылмаған. Каталогты қарап, позицияларды жоба тізіміне қосыңыз.",
    rfqDecreaseQtyAria: "Мөлшерін азайту",
    rfqIncreaseQtyAria: "Мөлшерін көбейту",
    rfqRemoveAria: "Жою",
    rfqPriceOnRequestShort: "Сұраныс бойынша",
    rfqTotal: "Барлығы",
    rfqContinue: "Жалғастыру",
    rfqBackToListText: "Тізімге оралу",
    rfqItemSingular: "позиция",
    rfqItemPlural: "позициялар",
    rfqFormName: "Аты-жөні",
    rfqFormCompany: "Компания",
    rfqFormPhone: "Телефон",
    rfqFormEmail: "Байланыс email",
    rfqFormNotes: "Жоба бойынша ескертпелер",
    rfqSubmit: "Шот сұрау",
    rfqFormError: "Сұранысты жіберу мүмкін болмады. Қайталап көріңіз.",
    rfqAccountMissingInfoError:
      "Аккаунтыңызда байланыс деректері толтырылмаған. Сұраныс жіберу үшін +7 771 182 93 08 нөміріне хабарласыңыз.",
    rfqSessionExpiredError: "Сессия аяқталды. Сұранысты жіберу үшін төмендегі байланыс деректерін толтырыңыз.",

    // Auth (Login / Register / Reset)
    authPasswordMismatch: "Құпия сөздер сәйкес келмейді",
    authCheckEmailTitle: "Поштаңызды тексеріңіз",
    authCheckEmailMessage:
      "Растау хатын {email} мекенжайына жібердік. Тіркеуді аяқтау үшін хаттағы сілтемеге өтіңіз.",
    authResendVerification: "Хатты қайта жіберу",
    authResendSentMessage: "Хат қайта жіберілді.",
    authEmailNotVerifiedError: "Кіру үшін email-ды растаңыз.",
    authRegisterEmailSendError:
      "Аккаунт құрылды, бірақ растау хаты жіберілмеді. Тіркеуді аяқтау үшін бізбен байланысыңыз.",
    verifyEmailLoading: "Email-ды растап жатырмыз…",
    verifyEmailInvalidTitle: "Сілтеме жарамсыз",
    verifyEmailInvalidDescription:
      "Бұл сілтеменің мерзімі өтіп кеткен немесе ол бұрын қолданылған. Төменде жаңа сілтеме сұраңыз.",
    verifyEmailResendIntro: "Жаңа растау сілтемесін алу үшін email-ды енгізіңіз.",
    authEmailFormatHint: "Email-ды name@company.com форматында енгізіңіз",
    rfqLoginRequiredMessage: "Коммерциялық ұсыныс сұрау үшін жеке кабинетке кіріңіз.",
    rfqLoginRequiredCta: "Кіру",
    rfqGuestLoginHint: "Аккаунтыңыз бар ма?",
    rfqGuestLoginCta: "Кіріңіз",
    loginHeroTitle1: "Бізге қосылыңыз",
    loginHeroTitle2: "премиум ас үй шешімдері үшін",
    loginHeroDesc:
      "Жабдық тізімін сақтаңыз, оған кез келген құрылғыдан оралыңыз және жобаңызға сай жеке ұсыныстар алыңыз.",
    authTabLogin: "Кіру",
    authTabRegister: "Тіркелу",
    authEmailLabel: "Email",
    authPasswordLabel: "Құпия сөз",
    authForgotPassword: "Құпия сөзді ұмыттыңыз ба?",
    authSubmitLogin: "Кіру",
    authNameLabel: "Аты-жөні",
    authPhoneLabel: "Телефон нөмірі",
    authCompanyLabel: "Компания / Мейрамхана",
    authConfirmPasswordLabel: "Құпия сөзді растаңыз",
    authSubmitRegister: "Тіркелу",
    authBackToLogin: "Кіруге оралу",
    authResetSentMessage:
      "Егер «{email}» электрондық поштасына тіркелген аккаунт бар болса, оған құпия сөзді қалпына келтіру сілтемесін жібердік. Поштаңызды тексеріңіз.",
    authForgotIntro:
      "Тіркелу кезінде көрсетілген email мекенжайын енгізіңіз — біз оған құпия сөзді қалпына келтіру сілтемесін жібереміз.",
    authSubmitSendLink: "Сілтемені жіберу",
    authAdminLoginPrefix: "Әкімші үшін кіру —",
    authAdminLoginLinkText: "осында",
    resetPageTitle: "Жаңа құпия сөз",
    resetHeroTitle1: "Кабинетіңізге",
    resetHeroTitle2: "қол жеткізіңіз",
    resetHeroDesc:
      "Жаңа құпия сөзді орнатыңыз — сақтағаннан кейін кез келген құрылғыдан кіре аласыз.",
    resetInvalidTitle: "Сілтеме жарамсыз",
    resetInvalidDescription:
      "Бұл сілтеменің мерзімі өтіп кеткен немесе ол бұрын қолданылған. Құпия сөзді қалпына келтіруді кіру бетінен қайта сұраңыз.",
    resetInvalidLinkPrefix: "Сілтеме жарамсыз. Құпия сөзді қалпына келтіруді",
    resetLoginPageLinkText: "кіру бетінен",
    resetSuccessTitle: "Құпия сөз өзгертілді",
    resetSuccessMessage: "Құпия сөз сәтті өзгертілді. Енді жаңа құпия сөзбен кіре аласыз.",
    resetToLoginLink: "Кіруге өту",
    resetNewPasswordLabel: "Жаңа құпия сөз",
    resetPasswordHint: "Кемінде 6 таңба",
    resetShowPassword: "Құпия сөзді көрсету",
    resetHidePassword: "Құпия сөзді жасыру",
    resetPasswordsMatch: "Құпия сөздер сәйкес келеді",
    resetSubmitting: "Сақталуда…",
    resetSubmit: "Жаңа құпия сөзді сақтау",

    // Account
    accountPageTitle: "Жеке кабинет",
    accountLoggedInAs: "Сіз кірдіңіз:",
    accountRecommendationsTitle: "Сізге ұсынамыз",
    accountRecommendationsEmpty:
      "Әзірге жеке ұсыныстар жоқ. Каталогты қараңыз, біз сізге қолайлы жабдықты таңдап береміз.",
    accountRequestsTitle: "Менің сұрауларым",
    accountRequestsEmpty: "Әзірге сұрауларыңыз жоқ. Жабдықты жобаға қосып, коммерциялық ұсыныс сұраңыз.",
    accountRequestItemsCount: "Позициялар:",
    accountRequestDownload: "Ұсынысты жүктеу",
    accountRequestsColumnRequest: "Сұрау",
    accountRequestsColumnDate: "Күні",
    accountRequestsColumnAmount: "Сомасы",
    accountRequestsColumnStatus: "Мәртебесі",
    accountRequestsColumnDocuments: "Шоттар / Құжаттар",
    accountRequestsColumnDetails: "Толығырақ",
    accountRequestDocumentsPending: "Дайындалуда",
    requestStatusNew: "Өңделуде",
    requestStatusInReview: "Қаралуда",
    requestStatusOfferSent: "Ұсыныс дайын",
    requestStatusCancelled: "Бас тартылды",
    accountRequestCancel: "Сұрауды бас тарту",
    accountRequestCancelConfirm: "Осы сұрауды бас тартасыз ба? Бұл әрекетті болдырмау мүмкін емес.",
    accountRequestCancelError: "Сұрауды бас тарту мүмкін болмады. Қайталап көріңіз.",

    // Catalog toolbar / sidebar
    rangeFrom: "бастап",
    rangeTo: "дейін",
    resultsShownPrefix: "Көрсетілген",
    resultsShownOf: "барлығы",
    productCountSuffix: "тауар",
    sortLabel: "Сұрыптау",
    sortRelevance: "Релеванттылық бойынша",
    sortPriceAsc: "Арзаннан қымбатқа",
    sortPriceDesc: "Қымбаттан арзанға",
    sortNameAsc: "Атауы бойынша (А-Я)",
    sortNameDesc: "Атауы бойынша (Я-А)",
    sortNewest: "Жаңалықтар",
    clearAllFilters: "Барлығын тазалау",
    categoryFacetTitle: "Санат",
    brandFacetTitle: "Бренд",
    characteristicsFacetTitle: "Сипаттамалар",
    collapseFacet: "Жию",
    expandFacet: "Толығырақ көрсету",
    brandSearchPlaceholder: "Бренд іздеу",
    brandSearchNoResults: "Ештеңе табылмады",
    brandPickerTrigger: "Брендті таңдау",
    recommendedLabel: "Барлық тауарлар",

    // Misc toasts / errors
    rfqToastAdded: "Жобаға қосылды",
    authGenericError: "Сұранысты орындау мүмкін болмады",

    // 404
    notFoundEyebrow: "404",
    notFoundTitle: "Бет табылмады",
    notFoundDescription: "Бет жылжытылған немесе жойылған болуы мүмкін. Мекенжайды тексеріңіз немесе басты бетке оралыңыз.",
    notFoundCtaHome: "Басты бетке",
    notFoundCtaCatalog: "Каталогты қарау",
  },
} satisfies Record<Language, Record<string, string>>;

export type TranslationKey = keyof typeof TRANSLATIONS.ru;

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    let stored = null;
    try {
      stored = typeof window !== "undefined" ? localStorage.getItem("barcraft-language") : null;
    } catch (e) {
      // Ignore
    }
    return stored === "kz" ? "kz" : "ru";
  });

  useEffect(() => {
    try {
      localStorage.setItem("barcraft-language", language);
    } catch (e) {
      // Ignore
    }
  }, [language]);

  const t = (key: TranslationKey) => TRANSLATIONS[language][key];

  return (
    <LanguageContext.Provider value={{ language, setLanguage: setLanguageState, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
